import { DateTimePicker } from "@mui/x-date-pickers";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { ruRU } from "@mui/x-date-pickers/locales";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { renderTimeViewClock } from "@mui/x-date-pickers/timeViewRenderers";

import React, { useState } from "react";
import {
  useCreateEducationalPromotionMutation,
  useStudyCenterEducationalPromotionQuery,
  useUpdateEducationalPromotionMutation,
} from "@lib/study-center";
import { useNavigate, useParams, useLocation } from "react-router-dom";

import { BodyNormal } from "@ui/fonts";
import { Button } from "@ui/button";
import { BackButton } from "@components/back-button";
import Dropzone from "@ui/dropzone";
import { RichEditor } from "@ui/rich-editor";
import { handleApiError } from "@utils/handleApiError";
import moment, { Moment } from "moment";
import styles from "./create-educational-promotion.module.scss";

type CreateEducationalPromotionParams = {
  id?: string;
};

export const CreateEducationalPromotion: React.FC = () => {
  const history = useNavigate();
  const location = useLocation();
  const educationalPromotionId = useParams<CreateEducationalPromotionParams>().id;

  const id =
    typeof educationalPromotionId === "undefined"
      ? undefined
      : +educationalPromotionId;
  const { educationalPromotion } = useStudyCenterEducationalPromotionQuery(id);

  const createEducationalPromotionMutation = useCreateEducationalPromotionMutation();
  const updateEducationalPromotionMutation = useUpdateEducationalPromotionMutation();

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [title, setTitle] = useState(educationalPromotion?.title ?? "");
  const [date, setDate] = useState<Moment | null>(
    educationalPromotion?.date ? moment(educationalPromotion?.date) : moment()
  );
  const [description, setDescription] = useState(educationalPromotion?.description ?? "");
  const [shortDescription, setShortDescription] = useState(educationalPromotion?.shortDescription ?? "");
  const [files, setFiles] = useState<File[]>([]);

  const isEdit = !location.pathname.endsWith("/create");

  const handleUploadFiles = (files: File[]) => {
    setFiles((prev) => [...prev, ...files]);
  };

  const handleDeleteFile = (file: File) => {
    setFiles((files) => files.filter((_file) => _file.name !== file.name));
  };

  const onSubmit = (e: React.SubmitEvent) => {
    e.preventDefault();

    const onSuccess = () => {
      history("/study-center/materials/educational-promotions");
    };

    const onError = (error: unknown) => {
      handleApiError(error, setErrors);
    };

    if (isEdit) {
      updateEducationalPromotionMutation.mutate(
        {
          id,
          title,
          shortDescription,
          description,
          file: files[0],
          date: date?.toISOString(),
        },
        {
          onSuccess,
          onError,
        }
      );
    } else {
      createEducationalPromotionMutation.mutate(
        {
          title,
          shortDescription,
          description,
          file: files[0],
          date: date ? date.toISOString() : moment().toISOString(),
        },
        {
          onSuccess,
          onError,
        }
      );
    }
  };

  const handleContent = (content: string) => {
    setDescription(content);
  };

  return (
    <div className={styles.EducationalPromotion}>
      <BackButton className="margin_bottom" />
      <form className={styles.EducationalPromotion__form} onSubmit={onSubmit}>
        <input
          placeholder="Название акции и предложения*"
          required
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          className={styles.EducationalPromotion__input}
        />
        <input
          placeholder="Краткое описание акции и предложения*"
          required
          maxLength={255}
          value={shortDescription}
          onChange={(e) => setShortDescription(e.target.value)}
          className={styles.EducationalPromotion__input}
        />
        <LocalizationProvider
          dateAdapter={AdapterMoment}
          adapterLocale={"ru"}
          localeText={
            ruRU.components.MuiLocalizationProvider.defaultProps.localeText
          }
        >
          <DateTimePicker
            value={date}
            disablePast
            onChange={setDate}
            label="Начало"
            format="DD.MM.YYYY HH:mm"
            autoFocus={false}
            viewRenderers={{
              hours: renderTimeViewClock,
              minutes: renderTimeViewClock,
              seconds: renderTimeViewClock,
            }}
            slotProps={{
              actionBar: { actions: ["cancel", "accept"] },
              textField: { variant: "filled" },
            }}
            localeText={{
              toolbarTitle: "",
              cancelButtonLabel: "Отменить",
              okButtonLabel: "ОК",
            }}
          />
        </LocalizationProvider>
        <RichEditor
          label="Описание*"
          initialContent={description || ""}
          handleContent={handleContent}
        />
        <Dropzone
          files={files}
          maxFiles={1}
          withPreviews
          accept="image/*"
          onUpload={handleUploadFiles}
          onDeleteFile={handleDeleteFile}
        />
        {errors.upload && <BodyNormal color="red">{errors.upload}</BodyNormal>}
        <Button
          type="submit"
          className={styles.EducationalPromotion__submitBtn}
          disabled={
            createEducationalPromotionMutation.isLoading ||
            updateEducationalPromotionMutation.isLoading
          }
        >
          Сохранить
        </Button>
      </form>
    </div>
  );
};
